import { makeStyles, tokens, mergeClasses } from '@fluentui/react-components';
import {
  ArrowUpRegular,
  ArrowDownRegular,
  SubtractRegular,
} from '@fluentui/react-icons';
import type { ReactNode } from 'react';

import { spacing, statusPalette } from '@/theme/tokens';

import { formatValue } from './format';
import type { ChartUnit } from './types';

interface KpiTileProps {
  /** Short metric label, e.g. "DAU" or "Cosmos RU/s". */
  label: string;
  /** Current value. Null renders as unavailable. */
  value: number | null;
  unit?: ChartUnit;
  /** Optional comparison value — drives the trend chip + delta. */
  previousValue?: number;
  /** Optional non-blocking loading state while the tile's data refreshes. */
  loading?: boolean;
  /** Inverts the trend coloring (lower is better, e.g. error rate, p95 latency). */
  lowerIsBetter?: boolean;
  /** Optional small status tint applied to the value text. */
  tone?: 'neutral' | 'healthy' | 'degraded' | 'unhealthy';
  /** Optional context line under the value. */
  caption?: string;
  /** Optional inline children (e.g. a sparkline) rendered below. */
  children?: ReactNode;
  /** If provided, the tile becomes interactive (button role) and calls this on click. */
  onClick?: () => void;
}

const useStyles = makeStyles({
  tile: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
    padding: spacing.l,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    minHeight: '100px',
  },
  tileInteractive: {
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  label: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    fontWeight: tokens.fontWeightSemibold,
  },
  valueRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: spacing.s,
  },
  value: {
    fontSize: tokens.fontSizeHero700,
    lineHeight: tokens.lineHeightHero700,
    fontWeight: tokens.fontWeightSemibold,
    fontVariantNumeric: 'tabular-nums',
  },
  valueHealthy: { color: statusPalette.healthy },
  valueDegraded: { color: statusPalette.degraded },
  valueUnhealthy: { color: statusPalette.unhealthy },
  trend: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing.xxs,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  trendUp: { color: statusPalette.healthy },
  trendDown: { color: statusPalette.unhealthy },
  trendFlat: { color: tokens.colorNeutralForeground3 },
  caption: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  body: {
    marginTop: spacing.xs,
  },
});

const FLAT_THRESHOLD = 0.005;
const PERCENT = 100;

export function KpiTile({
  label,
  value,
  unit,
  previousValue,
  loading = false,
  lowerIsBetter = false,
  tone = 'neutral',
  caption,
  children,
  onClick,
}: KpiTileProps) {
  const classes = useStyles();
  const numericValue = value ?? Number.NaN;

  const valueClass = mergeClasses(
    classes.value,
    tone === 'healthy' ? classes.valueHealthy : undefined,
    tone === 'degraded' ? classes.valueDegraded : undefined,
    tone === 'unhealthy' ? classes.valueUnhealthy : undefined,
  );

  let trendNode: ReactNode = null;
  if (!loading && Number.isFinite(numericValue) && previousValue !== undefined && Number.isFinite(previousValue) && previousValue !== 0) {
    const delta = (numericValue - previousValue) / Math.abs(previousValue);
    const flat = Math.abs(delta) < FLAT_THRESHOLD;
    const up = delta > 0;
    const positive = flat ? false : lowerIsBetter ? !up : up;
    // Renamed to avoid shadowing the component's `label` prop.
    const trendLabel = `${up ? '+' : ''}${(delta * PERCENT).toFixed(1)}%`;
    const trendClass = mergeClasses(
      classes.trend,
      flat ? classes.trendFlat : positive ? classes.trendUp : classes.trendDown,
    );
    trendNode = (
      <span className={trendClass} aria-label={`Change vs previous: ${trendLabel}`}>
        {flat ? <SubtractRegular /> : up ? <ArrowUpRegular /> : <ArrowDownRegular />}
        {trendLabel}
      </span>
    );
  }

  return (
    <div
      className={mergeClasses(classes.tile, onClick ? classes.tileInteractive : undefined)}
      role={onClick ? 'button' : undefined}
      aria-busy={loading || undefined}
      // When the tile is interactive, omit aria-label so the accessible name
      // is derived from the tile's text content (label + value + trend), giving
      // screen readers full KPI context rather than just "View details for …".
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => { if (e.key === ' ') e.preventDefault(); if ((e.key === 'Enter' || e.key === ' ') && !e.repeat) onClick(); } : undefined}
    >
      <span className={classes.label}>{label}</span>
      <div className={classes.valueRow}>
        <span className={valueClass}>{loading ? 'Loading…' : formatValue(numericValue, unit)}</span>
        {trendNode}
      </div>
      {caption && !loading ? <span className={classes.caption}>{caption}</span> : null}
      {children && !loading ? <div className={classes.body}>{children}</div> : null}
    </div>
  );
}
